import { Box } from "@gemini/core";
import { Button } from "@gemini/ui";
import { CloseIcon } from "@gemini/icons";
import { useEffect, useEffectEvent } from "react";

import { STATIC_ASSETS_PREFIX } from "../../../../constants/assets";
import { useTranslate } from "../../../../utils/useTranslate";
import { useCustomBannerTracking } from "../useCustomBannerTracking";

import type { CustomBannerProps } from "./types";

export const BottomCustomBanner = ({
  bannerInfo,
  onClick,
  onDismiss,
  isBannerVisible,
}: CustomBannerProps) => {
  const t = useTranslate();
  const { onDisplayEvent } = useCustomBannerTracking();
  const onDisplayEventStable = useEffectEvent(onDisplayEvent);

  useEffect(() => {
    onDisplayEventStable();
  }, []);

  const { translation, storeUrl } = bannerInfo;
  const title = t(`customAppBanner.${translation}.mobile.title`);
  const description = t(`customAppBanner.${translation}.mobile.description`);

  return (
    <Box
      /**
       * This custom attribute is used by React Aria to identify
       * top layers in the application (like modals, banners, etc.).
       *
       * See https://github.com/adobe/react-spectrum/issues/8784#issuecomment-3415368636
       */
      data-react-aria-top-layer="true"
      position="fixed"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      overflow="hidden"
      gap="spacing.24"
      bottom="spacing.none"
      right="spacing.none"
      zIndex="zIndex.4"
      width="100%"
      height="fit-content"
      pointerEvents="all"
      backgroundColor="color.background.light"
      borderColor="color.content.interactive.inverted.default"
      borderTopStyle="solid"
      borderTopWidth="borderWidth.1"
      borderTopLeftRadius="radius.16"
      borderTopRightRadius="radius.16"
      paddingTop="spacing.40"
      paddingBottom="spacing.40"
      boxShadow="shadow.32"
      color="color.content.active.default"
      transitionDuration="motionDuration.300"
      transitionTimingFunction="motionEasing.ease-out"
      transitionProperty="transform"
      transform={
        isBannerVisible
          ? "translate3d(0, 0, 0)"
          : "translate3d(0, calc(100% + 2rem), 0)"
      }
    >
      {/* Close Button */}
      <Box
        position="absolute"
        top="0"
        right="0"
        padding="spacing.4"
      >
        <Button
          size="32"
          icon={CloseIcon}
          iconOnly
          onPress={() => {
            onDismiss();
          }}
          type="button"
          variant="tertiary"
        >
          Close
        </Button>
      </Box>

      {/* Logo */}
      <Box
        as="img"
        src={`${STATIC_ASSETS_PREFIX}/custom-banner/logo-brand.svg`}
        alt="Brand Logo"
        alignSelf="center"
      />

      {/* Text & Button CTA */}
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        width="100%"
        paddingLeft="spacing.16"
        paddingRight="spacing.16"
      >
        <Box
          textAlign="center"
          paddingBottom="spacing.6"
          typography="typography.headline.20.bold"
        >
          {title}
        </Box>
        <Box
          textAlign="center"
          paddingBottom="spacing.24"
          typography="typography.body.14.regular"
        >
          {description}
        </Box>
        <Button
          onPress={() => {
            window.open(storeUrl, "_blank", "noopener,noreferrer");
            onClick("CTA");
          }}
        >
          {t(`customAppBanner.openTheApp`)}
        </Button>
      </Box>
    </Box>
  );
};
